import { memo, useMemo } from "react";
import { Box } from "@/components";
import type { RedemptionPosition, UserRedemptionsData } from "@/services";
import { RedemptionPositionsList } from "./RedemptionPositionsList";
import { RedemptionQueueHeader } from "./RedemptionQueueHeader";
import { RedemptionQueueSkeleton } from "./skeletons/RedemptionQueueSkeleton";

interface RedemptionQueueContentProps {
    redemptionsData: UserRedemptionsData | null;
    isLoading: boolean;
}

export const RedemptionQueueContent = memo(function RedemptionQueueContent({
    redemptionsData,
    isLoading,
}: RedemptionQueueContentProps) {
    const positions = redemptionsData?.positions ?? [];

    const displayPositions = useMemo(
        () =>
            positions.filter(
                (position: RedemptionPosition) => position.status === "queued" || position.status === "funded",
            ),
        [positions],
    );
    const fundedPositions = useMemo(
        () => displayPositions.filter((position: RedemptionPosition) => position.status === "funded"),
        [displayPositions],
    );
    const queuedPositions = useMemo(
        () =>
            displayPositions
                .filter((position: RedemptionPosition) => position.status === "queued")
                .sort((left, right) => left.queuePosition - right.queuePosition),
        [displayPositions],
    );
    const sortedPositions = useMemo(
        () => [
            ...[...fundedPositions].sort(
                (left, right) => (right.fundedTimestampMs ?? 0) - (left.fundedTimestampMs ?? 0),
            ),
            ...queuedPositions,
        ],
        [fundedPositions, queuedPositions],
    );

    const totalQueuedAmount = useMemo(() => {
        const derivedTotal = queuedPositions.reduce((total, position) => total + Number(position.amountNormalized), 0);
        return Math.max(Number(redemptionsData?.totalQueuedNormalized ?? 0), derivedTotal);
    }, [queuedPositions, redemptionsData?.totalQueuedNormalized]);

    const totalFundedAmount = useMemo(() => {
        const derivedTotal = fundedPositions.reduce(
            (total, position) => total + Number(position.claimableAmountNormalized ?? position.amountNormalized ?? 0),
            0,
        );
        return Math.max(Number(redemptionsData?.totalFundedNormalized ?? 0), derivedTotal);
    }, [fundedPositions, redemptionsData?.totalFundedNormalized]);

    if (isLoading) {
        return (
            <Box className="gap-6 rounded-[8px] p-[36px] border-primary/20 border-[1px]" card shadow>
                <RedemptionQueueSkeleton />
            </Box>
        );
    }

    const hasPositions = sortedPositions.length > 0;

    if (!hasPositions) {
        return null;
    }

    return (
        <Box className="gap-6 rounded-[8px] p-[36px] border-primary/20 border-[1px]" card shadow>
            <div className="space-y-4">
                <RedemptionQueueHeader
                    totalQueued={String(totalQueuedAmount)}
                    totalFunded={String(totalFundedAmount)}
                />
                <RedemptionPositionsList positions={sortedPositions} />
            </div>
        </Box>
    );
});

RedemptionQueueContent.displayName = "RedemptionQueueContent";
